const CSS = `
#prompt {
  position: fixed;
  left: 50%;
  bottom: calc(118px + env(safe-area-inset-bottom, 0px));
  z-index: 11;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  transform: translate(-50%, 8px);
  opacity: 0;
  transition: opacity 200ms ease, transform 260ms ease;
}
#prompt[data-visible="true"] {
  opacity: 1;
  transform: translate(-50%, 0);
}
#prompt .slot {
  position: relative;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
}
#prompt .ring {
  position: absolute;
  inset: 0;
  width: 30px;
  height: 30px;
  opacity: 0;
  transition: opacity 140ms ease;
}
#prompt[data-charging="true"] .ring {
  opacity: 1;
}
#prompt .ring rect {
  fill: none;
  /* dark against the daylight ground, light against the night — a cream ring
     disappears entirely on a lit field */
  stroke: #14120f;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 100.56;
  stroke-dashoffset: 100.56;
  filter: drop-shadow(0 0 3px rgba(9, 8, 7, 0.45));
}
#prompt[data-night="true"] .ring rect {
  stroke: #e9e2d0;
  filter: drop-shadow(0 0 3px rgba(233, 226, 208, 0.5));
}
#prompt .key {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  background: #e9e2d0;
  color: #14120f;
  font-family: "Silkscreen", monospace;
  font-size: 14px;
  line-height: 1;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.5);
}
#prompt .key .icon {
  display: none;
  width: 20px;
  height: 20px;
}
#prompt .key[data-icon="tap"] .letter {
  display: none;
}
#prompt .key[data-icon="tap"] .icon {
  display: block;
}
#prompt .label {
  padding: 5px 10px 6px;
  white-space: nowrap;
  border-radius: 2px;
  background: rgba(9, 8, 7, 0.86);
  color: #e9e2d0;
  font-family: "Silkscreen", monospace;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
`;

// perimeter of the rounded rect that hugs the keycap: 2(w - 2r) + 2(h - 2r) + 2*pi*r
const RING_LENGTH = 100.56;
// a touch point: a dot inside a ring reads at 20px where any drawn finger
// collapses into something else
const TAP_ICON = `
  <svg class="icon" viewBox="0 0 26 26" aria-hidden="true">
    <circle cx="13" cy="13" r="3.7" fill="#14120f" />
    <circle cx="13" cy="13" r="8.1" fill="none" stroke="#14120f" stroke-width="2.4" />
  </svg>
`;

export type PromptKey = "E" | "A" | "X" | "tap" | null;

const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(high, value));

/**
 * The one hold-to-confirm prompt. Party invites and world objects share it, so
 * the keycap, the ring and the wording stay identical wherever it is used.
 */
export class Prompt {
  private readonly root: HTMLDivElement;
  private readonly key: HTMLSpanElement;
  private readonly letter: HTMLSpanElement;
  private readonly label: HTMLSpanElement;
  private readonly progress: SVGRectElement;

  constructor() {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    this.root = document.createElement("div");
    this.root.id = "prompt";
    this.root.dataset.visible = "false";

    const slot = document.createElement("span");
    slot.className = "slot";
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "ring");
    svg.setAttribute("viewBox", "0 0 30 30");
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", "1.25");
    rect.setAttribute("y", "1.25");
    rect.setAttribute("width", "27.5");
    rect.setAttribute("height", "27.5");
    rect.setAttribute("rx", "5.5");
    svg.appendChild(rect);
    this.progress = rect;

    this.key = document.createElement("span");
    this.key.className = "key";
    this.key.innerHTML = `<span class="letter"></span>${TAP_ICON}`;
    this.letter = this.key.firstElementChild as HTMLSpanElement;
    slot.append(svg, this.key);

    this.label = document.createElement("span");
    this.label.className = "label";

    this.root.append(slot, this.label);
    document.body.appendChild(this.root);
  }

  setNight(on: boolean): void {
    this.root.dataset.night = on ? "true" : "false";
  }

  set(text: string | null, key: PromptKey, progress: number): void {
    const clamped = clamp(progress, 0, 1);
    this.progress.style.strokeDashoffset = `${RING_LENGTH * (1 - clamped)}`;
    this.root.dataset.charging = clamped > 0.02 ? "true" : "false";

    if (text === null) {
      this.root.dataset.visible = "false";
      return;
    }
    if (this.label.textContent !== text) this.label.textContent = text;

    if (key === null) {
      this.key.style.display = "none";
    } else {
      this.key.style.display = "";
      this.key.dataset.icon = key === "tap" ? "tap" : "";
      if (key !== "tap") this.letter.textContent = key;
    }
    this.root.dataset.visible = "true";
  }
}
